import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useGetMeQuery, useUpdateVacationMutation } from 'store/services/user/meApi';

export interface VacationModeApi {
  active: boolean;
  /** 00:00Z of the return date, or null for no end date. Display with formatVacationDate. */
  until: string | null;
  isSaving: boolean;
  /** `until` is a local calendar date (YYYY-MM-DD) or null for no end date. */
  turnOn: (until: string | null) => void;
  turnOff: () => void;
}

/**
 * Vacation Mode state and writes, read from the cached current user so the
 * Settings card, the app-wide banner and the status chips always agree.
 * A rejected write rolls back (the mutation undoes its patch) and shows the
 * server's message, e.g. a return date whose day has already started in UTC.
 */
export const useVacationMode = (): VacationModeApi => {
  const { data: me } = useGetMeQuery();
  const [updateVacation, { isLoading }] = useUpdateVacationMutation();

  const write = useCallback(
    (enabled: boolean, until: string | null) => {
      updateVacation({ enabled, until })
        .unwrap()
        .catch((error: any) => {
          toast.error(error?.data?.message || 'Failed to update Vacation Mode. Please try again.');
        });
    },
    [updateVacation],
  );

  const turnOn = useCallback((until: string | null) => write(true, until), [write]);
  const turnOff = useCallback(() => write(false, null), [write]);

  return {
    active: !!me?.vacationMode,
    until: me?.vacationUntil ?? null,
    isSaving: isLoading,
    turnOn,
    turnOff,
  };
};

export default useVacationMode;
